import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  useCharterPortfolio,
  useCharterServices,
  useCreatePortfolioWork,
  useDeletePortfolioWork,
  useUpdatePortfolioWork,
} from '../hooks/useCharter';
import { PortfolioWorkForm } from '../components/PortfolioWorkForm';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
} from '../components/CharterPage';
import type { CreatePortfolioWorkPayload, PortfolioWork } from '../types';

export function CharterPortfolioPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const { data: works = [], isLoading, error } = useCharterPortfolio(selectedTenantId);
  const { data: services = [] } = useCharterServices(selectedTenantId);
  const createWork = useCreatePortfolioWork();
  const updateWork = useUpdatePortfolioWork();
  const deleteWork = useDeletePortfolioWork();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PortfolioWork | null>(null);

  const isSaving = createWork.isPending || updateWork.isPending;

  const submit = async (values: CreatePortfolioWorkPayload) => {
    if (editing) await updateWork.mutateAsync({ id: editing.id, data: values });
    else await createWork.mutateAsync(values);
    setModalOpen(false);
    setEditing(null);
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Portfolio"
        subtitle="Showcase past work. Clients can book or order 'from' a piece, which then appears as inspiration on the request."
        action={
          canManage && selectedTenantId ? (
            <Button
              size="sm"
              className="h-[40px]"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New work
            </Button>
          ) : undefined
        }
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter portfolio" />
      ) : isLoading ? (
        <LoadingState label="Loading portfolio…" />
      ) : error ? (
        <ErrorState label="Failed to load portfolio." />
      ) : works.length === 0 ? (
        <EmptyState label={`No portfolio works yet.${canManage ? ' Click "New work" to add one.' : ''}`} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {works.map((w) => (
            <div key={w.id} className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="aspect-video bg-muted overflow-hidden">
                {w.media[0] ? (
                  <img src={w.media[0].cdnUrl} alt={w.title} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">No media</div>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-medium text-foreground">{w.title}</p>
                {w.caption && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{w.caption}</p>}
                {canManage && (
                  <div className="flex gap-4 mt-3">
                    <button
                      onClick={() => {
                        setEditing(w);
                        setModalOpen(true);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${w.title}"?`)) deleteWork.mutate(w.id);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit work' : 'New portfolio work'}</h2>
            <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <PortfolioWorkForm
            initial={editing}
            services={services}
            onSubmit={submit}
            onCancel={() => setModalOpen(false)}
            isSaving={isSaving}
          />
        </div>
      </Modal>
    </div>
  );
}
